import React from 'react';
import Plot from 'react-plotly.js';
import { Box, Typography } from '@mui/material';

const ForestPlot = ({ associations }) => {
  // Collect OR data with confidence intervals
  const plotData = [];

  // Helper function to estimate CI from OR and p-value
  const estimateCI = (or, pval) => {
    if (!pval || pval <= 0 || pval >= 1) {
      // No p-value, use conservative estimate
      return { lower: or * 0.5, upper: or * 2.0 };
    }
    
    // Approximate Z-score from p-value (two-tailed)
    // For p=0.05, z≈1.96; for p=0.01, z≈2.58; for p=0.001, z≈3.29
    let z = 1.96;
    if (pval < 0.001) z = 3.29;
    else if (pval < 0.01) z = 2.58;
    else if (pval < 0.05) z = 1.96;
    else z = 1.645; // p < 0.1
    
    // Estimate SE from log(OR) and Z-score
    const logOR = Math.log(or);
    const se = Math.abs(logOR / z);
    
    // Calculate CI in log space, then convert back
    const lower = Math.exp(logOR - 1.96 * se);
    const upper = Math.exp(logOR + 1.96 * se);
    
    return { lower, upper };
  };

  // Helper function to calculate OR from de novo counts (for de novo studies)
  // This estimates OR when we have case/control de novo counts but need total sample sizes
  const calculateDnOR = (caseDnCount, controlDnCount) => {
    // Estimate OR from de novo mutation rates
    // If we have actual de novo counts, we can estimate rates
    // For a typical trio study: cases might have ~1000 trios, controls ~2000
    // This is approximate - actual OR would need known sample sizes
    if (caseDnCount === 0 && controlDnCount === 0) return null;
    if (controlDnCount === 0) return 50; // High OR estimate
    if (caseDnCount === 0) return 0.1; // Low OR estimate
    
    // Estimate based on ratio of de novo counts (conservative estimate)
    // This assumes roughly similar total sample sizes
    // For ASC: if we have case_dn=9 and control_dn=0, estimate high OR
    // Using observed/expected ratio similar to NDD approach
    const estimatedOR = (caseDnCount / Math.max(controlDnCount, 1)) * 2; // Multiply by 2 as rough estimate
    return Math.max(0.1, Math.min(100, estimatedOR)); // Cap at reasonable bounds
  };

  // ASC associations - calculate OR from de novo counts
  // Always show ASC entry (use OR=0 if no data)
  const ascData = associations.asc?.[0] || {};
  const caseDn = ascData.case_dn_ptv || 0;
  const controlDn = ascData.control_dn_ptv || 0;
  
  if (caseDn > 0 || controlDn > 0) {
    const orEstimate = calculateDnOR(caseDn, controlDn);
    if (orEstimate && orEstimate > 0) {
      const { lower, upper } = estimateCI(orEstimate, ascData.qval || 0.05);
      plotData.push({
        disorder: 'ASC',
        subtype: 'De novo',
        mutType: 'PTV',
        or: orEstimate,
        lower: Math.max(lower, orEstimate * 0.2),
        upper: Math.max(upper, orEstimate * 3.0),
        pval: ascData.qval !== undefined && ascData.qval !== null ? ascData.qval : 0.05,
      });
    } else {
      // Show OR=0 if calculation failed
      plotData.push({
        disorder: 'ASC',
        subtype: 'De novo',
        mutType: 'PTV',
        or: 0,
        lower: 0,
        upper: 0,
        pval: ascData.qval !== undefined && ascData.qval !== null ? ascData.qval : 1.0,
      });
    }
  } else {
    // No data available - show OR=0
    plotData.push({
      disorder: 'ASC',
      subtype: 'De novo',
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: 1.0,
    });
  }

  // SCHEMA associations - include PTV, Class I (likely LoF), and Class II (missense)
  const schemaData = associations.schema?.find(s => s.group === 'meta') || associations.schema?.[0] || {};
  const schemaGroup = schemaData.group || 'meta';
  const schemaPval = schemaData.q_meta || schemaData.p_meta;
  
  // PTV (protein-truncating variants)
  if (schemaData.or_ptv && schemaData.or_ptv > 0 && schemaData.or_ptv_ci && schemaData.or_ptv_ci[0] > 0 && schemaData.or_ptv_ci[1] > 0) {
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'PTV',
      or: schemaData.or_ptv,
      lower: schemaData.or_ptv_ci[0],
      upper: schemaData.or_ptv_ci[1],
      pval: schemaPval || 1.0,
    });
  } else {
    // No data - show OR=0
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: schemaPval || 1.0,
    });
  }
  
  // Class I (likely loss-of-function) - only if available
  if (schemaData.or_class1 && schemaData.or_class1 > 0 && schemaData.or_class1_ci && 
      schemaData.or_class1_ci[0] > 0 && schemaData.or_class1_ci[1] > 0) {
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'Class I (LoF)',
      or: schemaData.or_class1,
      lower: schemaData.or_class1_ci[0],
      upper: schemaData.or_class1_ci[1],
      pval: schemaPval || 1.0,
    });
  } else {
    // No Class I data - show OR=0
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'Class I (LoF)',
      or: 0,
      lower: 0,
      upper: 0,
      pval: schemaPval || 1.0,
    });
  }
  
  // Class II (missense) - only if available
  if (schemaData.or_class2 && schemaData.or_class2 > 0 && schemaData.or_class2_ci && 
      schemaData.or_class2_ci[0] > 0 && schemaData.or_class2_ci[1] > 0) {
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'Class II (Missense)',
      or: schemaData.or_class2,
      lower: schemaData.or_class2_ci[0],
      upper: schemaData.or_class2_ci[1],
      pval: schemaPval || 1.0,
    });
  } else {
    // No Class II data - show OR=0
    plotData.push({
      disorder: 'SCHEMA',
      subtype: schemaGroup,
      mutType: 'Class II (Missense)',
      or: 0,
      lower: 0,
      upper: 0,
      pval: schemaPval || 1.0,
    });
  }

  // BipEx associations - include both PTV and damaging missense
  // Filter to only show "Bipolar Disorder" (main type)
  const bipexData = associations.bipex?.find(b => b.group === 'Bipolar Disorder') || associations.bipex?.[0] || {};
  const bipexGroup = bipexData.group || 'Bipolar Disorder';
  
  // PTV (protein-truncating variants)
  if (bipexData.ptv_or && bipexData.ptv_or > 0 && bipexData.ptv_pval) {
    const { lower, upper } = estimateCI(bipexData.ptv_or, bipexData.ptv_pval);
    plotData.push({
      disorder: 'BipEx',
      subtype: bipexGroup,
      mutType: 'PTV',
      or: bipexData.ptv_or,
      lower: Math.max(lower, bipexData.ptv_or * 0.1),
      upper: Math.max(upper, bipexData.ptv_or * 2.0),
      pval: bipexData.ptv_pval,
    });
  } else {
    // No data - show OR=0
    plotData.push({
      disorder: 'BipEx',
      subtype: bipexGroup,
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: bipexData.ptv_pval || 1.0,
    });
  }
  
  // Damaging missense
  if (bipexData.damaging_missense_or && bipexData.damaging_missense_or > 0 && bipexData.damaging_missense_pval) {
    const { lower, upper } = estimateCI(bipexData.damaging_missense_or, bipexData.damaging_missense_pval);
    plotData.push({
      disorder: 'BipEx',
      subtype: bipexGroup,
      mutType: 'Damaging Missense',
      or: bipexData.damaging_missense_or,
      lower: Math.max(lower, bipexData.damaging_missense_or * 0.1),
      upper: Math.max(upper, bipexData.damaging_missense_or * 2.0),
      pval: bipexData.damaging_missense_pval,
    });
  } else {
    // No damaging missense data - show OR=0
    plotData.push({
      disorder: 'BipEx',
      subtype: bipexGroup,
      mutType: 'Damaging Missense',
      or: 0,
      lower: 0,
      upper: 0,
      pval: bipexData.damaging_missense_pval || 1.0,
    });
  }

  // Helper to calculate OR from case/control counts (when OR is missing)
  const calculateOR = (caseCount, caseTotal, controlCount, controlTotal) => {
    if (caseCount === 0 && controlCount === 0) return null;
    if (caseCount === 0) return 0.1; // Very small OR
    if (controlCount === 0) return 1000; // Very large OR
    
    const caseRate = caseCount / caseTotal;
    const controlRate = controlCount / controlTotal;
    
    if (controlRate === 0) return 1000;
    if (caseRate === 0) return 0.1;
    
    const or = (caseRate / (1 - caseRate)) / (controlRate / (1 - controlRate));
    return Math.max(0.1, Math.min(1000, or)); // Cap at reasonable bounds
  };

  // Epi25 associations - include both PTV and damaging missense
  // Filter to only show "EPI" (main type)
  const epi25Data = associations.epi25?.find(e => e.group === 'EPI') || associations.epi25?.[0] || {};
  const epi25Group = epi25Data.group || 'EPI';
  
  // PTV (protein-truncating variants)
  let ptv_or = epi25Data.ptv_or;
  if (!ptv_or && epi25Data.ptv_pval && epi25Data.ptv_case_count !== undefined && epi25Data.ptv_control_count !== undefined) {
    // Calculate OR from counts if missing
    const caseCount = epi25Data.ptv_case_count || 0;
    const controlCount = epi25Data.ptv_control_count || 0;
    
    // Simple OR estimate when we have case/control counts but no totals
    // For control_count = 0, estimate high OR (similar to ASC approach)
    if (controlCount === 0 && caseCount > 0) {
      ptv_or = 50.0; // High estimate (conservative, not 1000)
    } else if (caseCount === 0) {
      ptv_or = 0.1; // Low estimate
    } else if (epi25Data.n_cases && epi25Data.n_controls) {
      // If we have totals, use proper calculation
      ptv_or = calculateOR(
        caseCount,
        epi25Data.n_cases,
        controlCount,
        epi25Data.n_controls
      );
    } else {
      // Estimate from ratio (conservative)
      ptv_or = (caseCount / Math.max(controlCount, 1)) * 2.0;
      ptv_or = Math.max(0.1, Math.min(100.0, ptv_or));
    }
  }
  
  if (ptv_or && ptv_or > 0 && epi25Data.ptv_pval) {
    const { lower, upper } = estimateCI(ptv_or, epi25Data.ptv_pval);
    plotData.push({
      disorder: 'Epi25',
      subtype: epi25Group,
      mutType: 'PTV',
      or: ptv_or,
      lower: Math.max(lower, ptv_or * 0.1),
      upper: Math.max(upper, ptv_or * 2.0),
      pval: epi25Data.ptv_pval,
    });
  } else {
    // No data - show OR=0
    plotData.push({
      disorder: 'Epi25',
      subtype: epi25Group,
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: epi25Data.ptv_pval || 1.0,
    });
  }
  
  // Damaging missense
  let missense_or = epi25Data.damaging_missense_or;
  if (!missense_or && epi25Data.damaging_missense_pval && 
      epi25Data.damaging_missense_case_count !== undefined && 
      epi25Data.damaging_missense_control_count !== undefined) {
    missense_or = calculateOR(
      epi25Data.damaging_missense_case_count,
      epi25Data.n_cases,
      epi25Data.damaging_missense_control_count,
      epi25Data.n_controls
    );
  }
  
  if (missense_or && missense_or > 0 && epi25Data.damaging_missense_pval) {
    const { lower, upper } = estimateCI(missense_or, epi25Data.damaging_missense_pval);
    plotData.push({
      disorder: 'Epi25',
      subtype: epi25Group,
      mutType: 'Damaging Missense',
      or: missense_or,
      lower: Math.max(lower, missense_or * 0.1),
      upper: Math.max(upper, missense_or * 2.0),
      pval: epi25Data.damaging_missense_pval,
    });
  } else {
    // No damaging missense data - show OR=0
    plotData.push({
      disorder: 'Epi25',
      subtype: epi25Group,
      mutType: 'Damaging Missense',
      or: 0,
      lower: 0,
      upper: 0,
      pval: epi25Data.damaging_missense_pval || 1.0,
    });
  }

  // MDD associations (PTV and missense are separate entries)
  const mddData = associations.mdd?.find(m => m.variant_type === 'ptv') || associations.mdd?.[0];
  if (mddData) {
    if (mddData.or && mddData.or > 0 && mddData.pval && mddData.or_ci && mddData.or_ci[0] > 0 && mddData.or_ci[1] > 0) {
      plotData.push({
        disorder: 'MDD',
        subtype: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        mutType: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        or: mddData.or,
        lower: mddData.or_ci[0],
        upper: mddData.or_ci[1],
        pval: mddData.pval,
      });
    } else if (mddData.or && mddData.or > 0 && mddData.pval) {
      const { lower, upper } = estimateCI(mddData.or, mddData.pval);
      plotData.push({
        disorder: 'MDD',
        subtype: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        mutType: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        or: mddData.or,
        lower: Math.max(lower, mddData.or * 0.1),
        upper: Math.max(upper, mddData.or * 2.0),
        pval: mddData.pval,
      });
    } else {
      // No data - show OR=0
      plotData.push({
        disorder: 'MDD',
        subtype: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        mutType: mddData.variant_type === 'ptv' ? 'PTV' : 'Missense',
        or: 0,
        lower: 0,
        upper: 0,
        pval: mddData.pval || 1.0,
      });
    }
  } else {
    // No MDD data at all - show OR=0
    plotData.push({
      disorder: 'MDD',
      subtype: 'PTV',
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: 1.0,
    });
  }

  // NDD associations
  if (associations.ndd && associations.ndd.denovowest_p_full) {
    const observed = associations.ndd.observed_full;
    const expected = associations.ndd.expected_full;
    if (observed !== null && expected !== null && expected > 0) {
      const or_estimate = observed / expected;
      const { lower, upper } = estimateCI(or_estimate, associations.ndd.denovowest_p_full);
      plotData.push({
        disorder: 'NDD',
        subtype: 'De novo',
        mutType: 'PTV (De novo)',
        or: or_estimate,
        lower: Math.max(lower, or_estimate * 0.1),
        upper: Math.max(upper, or_estimate * 5.0),
        pval: associations.ndd.denovowest_p_full,
      });
    } else {
      // No data - show OR=0
      plotData.push({
        disorder: 'NDD',
        subtype: 'De novo',
        mutType: 'PTV (De novo)',
        or: 0,
        lower: 0,
        upper: 0,
        pval: associations.ndd.denovowest_p_full || 1.0,
      });
    }
  } else {
    // No NDD data - show OR=0
    plotData.push({
      disorder: 'NDD',
      subtype: 'De novo',
      mutType: 'PTV (De novo)',
      or: 0,
      lower: 0,
      upper: 0,
      pval: 1.0,
    });
  }

  // ADHD associations
  if (associations.adhd) {
    if (associations.adhd.ptv_or && associations.adhd.ptv_or > 0 && associations.adhd.ptv_pval) {
      const ci = associations.adhd.ptv_or_ci || estimateCI(associations.adhd.ptv_or, associations.adhd.ptv_pval);
      plotData.push({
        disorder: 'ADHD',
        subtype: associations.adhd.group_name || 'ADHD',
        mutType: 'PTV',
        or: associations.adhd.ptv_or,
        lower: Array.isArray(ci) ? ci[0] : ci.lower,
        upper: Array.isArray(ci) ? ci[1] : ci.upper,
        pval: associations.adhd.ptv_pval,
      });
    } else {
      // No PTV data - show OR=0
      plotData.push({
        disorder: 'ADHD',
        subtype: associations.adhd.group_name || 'ADHD',
        mutType: 'PTV',
        or: 0,
        lower: 0,
        upper: 0,
        pval: associations.adhd.ptv_pval || 1.0,
      });
    }
    if (associations.adhd.mis_or && associations.adhd.mis_or > 0 && associations.adhd.mis_pval) {
      const ci = associations.adhd.mis_or_ci || estimateCI(associations.adhd.mis_or, associations.adhd.mis_pval);
      plotData.push({
        disorder: 'ADHD',
        subtype: associations.adhd.group_name || 'ADHD',
        mutType: 'Missense',
        or: associations.adhd.mis_or,
        lower: Array.isArray(ci) ? ci[0] : ci.lower,
        upper: Array.isArray(ci) ? ci[1] : ci.upper,
        pval: associations.adhd.mis_pval,
      });
    } else {
      // No missense data - show OR=0
      plotData.push({
        disorder: 'ADHD',
        subtype: associations.adhd.group_name || 'ADHD',
        mutType: 'Missense',
        or: 0,
        lower: 0,
        upper: 0,
        pval: associations.adhd.mis_pval || 1.0,
      });
    }
  } else {
    // No ADHD data at all - show OR=0
    plotData.push({
      disorder: 'ADHD',
      subtype: 'ADHD',
      mutType: 'PTV',
      or: 0,
      lower: 0,
      upper: 0,
      pval: 1.0,
    });
  }

  // Cognition associations - skip for now (multiple phenotypes, might want to filter)
  // Can add back later if needed, but for now focus on main disorders

  // OCD associations
  if (associations.ocd && associations.ocd.ptv_or && associations.ocd.ptv_or > 0 && associations.ocd.tada_pval) {
    const ci = associations.ocd.ptv_or_ci || estimateCI(associations.ocd.ptv_or, associations.ocd.tada_pval);
    plotData.push({
      disorder: 'OCD',
      subtype: associations.ocd.group_name || 'OCD+CTD',
      mutType: 'PTV (TADA)',
      or: associations.ocd.ptv_or,
      lower: Array.isArray(ci) ? ci[0] : ci.lower,
      upper: Array.isArray(ci) ? ci[1] : ci.upper,
      pval: associations.ocd.tada_pval,
    });
  } else {
    // No OCD data - show OR=0
    plotData.push({
      disorder: 'OCD',
      subtype: 'OCD+CTD',
      mutType: 'PTV (TADA)',
      or: 0,
      lower: 0,
      upper: 0,
      pval: associations.ocd?.tada_pval || 1.0,
    });
  }

  // Filter out entries with OR <= 0 (can't plot on log scale)
  const excludedDisorders = plotData.filter(d => !d.or || d.or <= 0).map(d => d.disorder);
  const filteredData = plotData.filter(d => d.or && d.or > 0);

  if (filteredData.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Insufficient data for forest plot (OR with valid CI required).
      </Typography>
    );
  }

  // Sort by OR (descending)
  filteredData.sort((a, b) => (b.or || 0) - (a.or || 0));

  // Replace plotData references below
  const plotDataFinal = filteredData;

  // Prepare data for Plotly - create descriptive labels
  const labels = plotDataFinal.map((d) => {
    let label = d.disorder;
    // For main types, don't show subtype in label (it's redundant)
    // Only show mutation type
    if (d.mutType) {
      label += ` (${d.mutType})`;
    }
    return label;
  });
  const ors = plotDataFinal.map((d) => d.or);
  const lowers = plotDataFinal.map((d) => d.lower);
  const uppers = plotDataFinal.map((d) => d.upper);

  // Calculate min/max for x-axis range (log scale)
  const allValues = [...ors, ...lowers, ...uppers].filter(v => v > 0);
  const minOR = Math.min(...allValues);
  const maxOR = Math.max(...allValues);
  
  // Set reasonable bounds with some padding (in log space)
  const logMin = Math.floor(Math.log10(minOR * 0.8));
  const logMax = Math.ceil(Math.log10(maxOR * 1.2));
  const xRange = [logMin, logMax];

  // Create shapes for confidence intervals (whiskers)
  const ciShapes = plotDataFinal.map((d, idx) => {
    return [
      // Horizontal line for CI
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: d.lower,
        x1: d.upper,
        y0: idx,
        y1: idx,
        line: { color: 'rgba(0,0,0,0.5)', width: 2 },
      },
      // Left whisker
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: d.lower,
        x1: d.lower,
        y0: idx - 0.2,
        y1: idx + 0.2,
        line: { color: 'rgba(0,0,0,0.5)', width: 2 },
      },
      // Right whisker
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: d.upper,
        x1: d.upper,
        y0: idx - 0.2,
        y1: idx + 0.2,
        line: { color: 'rgba(0,0,0,0.5)', width: 2 },
      },
    ];
  }).flat();

  // Color mapping function
  const getColor = (pval) => {
    if (!pval) return '#757575';
    if (pval < 5e-8) return '#d32f2f'; // Genome-wide significant
    if (pval < 1e-6) return '#ed6c02'; // Highly significant
    if (pval < 0.05) return '#0288d1'; // Significant
    return '#757575'; // Not significant
  };

  return (
    <Box>
      <Plot
        data={[
          {
            type: 'scatter',
            mode: 'markers',
            x: ors,
            y: plotDataFinal.map((_, idx) => idx),
            marker: {
              size: 14,
              color: plotDataFinal.map((d) => getColor(d.pval)),
              line: {
                width: 2,
                color: 'white',
              },
              symbol: 'diamond',
            },
            text: labels,
            hovertemplate: '<b>%{text}</b><br>' +
              'Mutation Type: %{customdata[3]}<br>' +
              'OR: %{x:.3f}<br>' +
              '95% CI: [%{customdata[0]:.3f}, %{customdata[1]:.3f}]<br>' +
              'p-value: %{customdata[2]:.2e}<extra></extra>',
            customdata: plotDataFinal.map((d) => [d.lower, d.upper, d.pval || 0, d.mutType || 'N/A']),
            showlegend: false,
          },
        ]}
        layout={{
          title: {
            text: 'Forest Plot: Odds Ratios (95% CI)',
            font: { size: 16 },
          },
          xaxis: {
            title: 'Odds Ratio (log scale)',
            type: 'log',
            range: xRange,
            showgrid: true,
            gridcolor: 'rgba(0,0,0,0.1)',
            zeroline: false,
          },
          yaxis: {
            title: '',
            tickmode: 'array',
            tickvals: plotDataFinal.map((_, idx) => idx),
            ticktext: labels,
            autorange: 'reversed',
            showgrid: false,
            zeroline: false,
          },
          height: Math.max(300, plotDataFinal.length * 60),
          margin: { l: 250, r: 50, t: 60, b: 50 },
          showlegend: false,
          shapes: [
            // Reference line at OR=1
            {
              type: 'line',
              xref: 'x',
              yref: 'paper',
              x0: 1,
              x1: 1,
              y0: 0,
              y1: 1,
              line: {
                color: 'gray',
                width: 2,
                dash: 'dash',
              },
            },
            // Confidence interval whiskers
            ...ciShapes,
          ],
        }}
        config={{ responsive: true, displayModeBar: true }}
        style={{ width: '100%' }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Note: Some confidence intervals are estimated from odds ratios and p-values when not directly reported by the study.
      </Typography>
      {excludedDisorders.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Disorders with missing or zero OR (not shown): {[...new Set(excludedDisorders)].join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export default ForestPlot;

